import Legend from '@/components/Legend/Legend'
import Spinner from '@/components/Spinner/Spinner'
import LineChart from './line-chart.js'
import { mapGetters } from 'vuex'

export default {
  name: 'Map',
  props: ['name', 'data', 'vehicleType', 'disableUI', 'zoom', 'tiny'],
  components: {
    Legend,
    Spinner,
    LineChart
  },
  computed: {
    ...mapGetters({
      mapMultiplier: 'mapMultiplier',
    })
  },
  data() {
    return {
      showMap: true,
      chartKey: 0,
      toggleButtonsKey: 0,
      map: null,
      mapLoading: false,
      bounds: null,
      initialized: false,
      currentPath: [],
      routePath: null,
      polyPoints: [],
      chartMapMapping: {},
      polyOptions: {
        geodesic: true,
        strokeColor: '#FF0000',
        strokeOpacity: 1.0,
        strokeWeight: 4
      },
      lastPolyIndex: null,
      clickedPolyIndices: [],
      selected: 'route',
      emissionOptions: [],
      currentEmissions: {},
      mapTriggeredHover: false,
      chartDataArchive: {
        labels: [],
        datasets: []
      },
      chartData: {
        labels: [],
        datasets: []
      },
      chartOptions: {
        animation: {
          duration: 0
        },
        hover: {
          animationDuration: 0
        },
        responsiveAnimationDuration: 0,
        responsive: true,
        maintainAspectRatio: false, // So we can control height
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false
            },
            ticks: {
              display: true
            }
          }],
          yAxes: []
        },
        tooltips: {
          enabled: true,
          mode: 'index',
          intersect: false,
          backgroundColor: '#000000',
          labelColors: 'red',
          callbacks: {
            labelColor: (tooltipItem, data) => {
              const color = (tooltipItem
                && data
                && data.data
                && data.data.datasets
                && data.data.datasets[tooltipItem.datasetIndex]
                && data.data.datasets[tooltipItem.datasetIndex].borderColor)
                ? data.data.datasets[tooltipItem.datasetIndex].borderColor
                : '#000000'
              return {
                borderColor: '#FFFFFF',
                backgroundColor: color
              }
            },
            label: (tooltipItem, data) => {
              let { label } = data.datasets[tooltipItem.datasetIndex]
              const { index } = tooltipItem
              if (label) label += ': '
              if (data.datasets[tooltipItem.datasetIndex]
                && data.datasets[tooltipItem.datasetIndex].data
                && data.datasets[tooltipItem.datasetIndex].data[index]) {
                const actual = data.datasets[tooltipItem.datasetIndex].data[index]
                label += actual
              } else {
                label += tooltipItem.yLabel
              }
              return label
            }
          }
        },
        events: ['mousemove', 'mouseout', 'click'],
        onHover: (event, item) => {
          if (this.lastPolyIndex && this.clickedPolyIndices.indexOf(this.lastPolyIndex) === -1) {
            if (!this.mapTriggeredHover) {
              google.maps.event.trigger(this.currentPath[this.lastPolyIndex], 'mouseout', {})
            }
            this.lastPolyIndex = null
          }
          if (item.length > 0) {
            const index = this.chartMapMapping[item[0]._index]
            const poly = this.currentPath[index]
            if (!this.mapTriggeredHover) {
              google.maps.event.trigger(poly, 'mouseover', {})
            }
            this.lastPolyIndex = index
          }
          this.mapTriggeredHover = false
        },
        onClick: (event, item) => {
          if (item.length > 0) {
            const index = item[0]._index
            google.maps.event.trigger(this.currentPath[index], 'mouseover', {})
            this.clickedPolyIndices.push(index)
          }
        }
      }
    }
  },
  beforeDestroy() {
    // Clear memory
    for (const poly of this.currentPath) {
      google.maps.event.clearListeners(poly, 'mouseover')
      google.maps.event.clearListeners(poly, 'mouseout')
    }
    delete this.map
    delete this.bounds
    delete this.routePath
    delete this.polyPoints
    delete this.currentPath
  },
  mounted() {
    this.mapLoading = true
    if (!this.tiny) {
      // Prepare for the chart
      this.emissionOptions = this.getOptions(this.vehicleType)
      for (const [index, option] of this.emissionOptions.entries()) {
        if (option.value !== 'route') {
          this.currentEmissions[option.value] = true
          this.chartDataArchive.datasets.push({ label: option.label, id: option.value, fill: false, yAxisID: option.value, borderColor: option.color, data: [] })
          this.chartOptions.scales.yAxes.push({ id: option.value, display: false })
        }
      }
      this.chartOptions.scales.yAxes.push({ id: 'dummy', display: true, offset: false, gridLines: { drawTicks: false }, ticks: { display: false } })
    }
    // Initialize map
    const element = document.getElementById(this.name)
    const options = {
      mapTypeId: 'terrain',
      center: new google.maps.LatLng(52.08274, 4.326433),
      zoom: parseFloat(this.zoom) || 10,
      disableDefaultUI: this.disableUI || false,
      gestureHandling: this.diasableUI ? 'none' : 'cooperative',
      zoomControl: !this.disableUI,
      scrollwheel: !this.disableUI,
      draggable: !this.disableUI
    }
    const map = new google.maps.Map(element, options)
    this.map = map
    this.bounds = new google.maps.LatLngBounds()
    // Resize map if the window resizes
    window.onresize = () => {
      map.fitBounds(this.bounds)
      if (this.zoom) map.setZoom(parseFloat(this.zoom))
    }
    // Init map if data is already available
    if (this.data && this.data.length) {
      // Disable the map if no GPS coords exist
      if (this.data.filter(x => x.gpslon).length === 0) this.showMap = false
      this.initialized = true
      this.initMap(this.data)
    }
  },
  watch: {
    selected(emission) {
      this.mapLoading = true
      this.redrawMap(emission)
      this.redrawChart(this.map)
    },
    data(datapoints) {
      if (datapoints === 'no_data') return this.mapLoading = false
      if (!this.initialized && datapoints.length) {
        // Disable the map if no GPS coords exist
        if (datapoints.filter(x => x.gpslon).length === 0) this.showMap = false
        this.initialized = true
        this.initMap(datapoints)
      }
    }
  },
  methods: {
    initMap(datapoints) {
      if (datapoints === 'no_data') return this.mapLoading = false

      const bounds = this.bounds
      const pathCoordinates = []
      let lastAcceptedCoordinate = null

      // Create chartData entry for each emission
      const emissionTypes =  this.getOptions(this.vehicleType)

      // Create entries for map and chart
      for (const [index, point] of datapoints.entries()) {

        // Define polyPoint, these values are what is available in the select box,
        // and what will be rendered on the map + in the chart
        const polyPoint = {
          co2_gs: isNaN(point.co2_mf) ? 'No data' : point.co2_mf, // CO2 [g/s]
          co2_cumulative_gkm: isNaN(point.co2_cumulative_average) ? 'No data': point.co2_cumulative_average, // CO2 cumulative average [g/km]
          ect_c: isNaN(point.ect) ? 'No data' : point.ect,
          egt_c: isNaN(point.after_cat_egt) ? 'No data' : point.after_cat_egt,
          nh3_ppm: isNaN(point.after_cat_nh3) ? 'No data' : point.after_cat_nh3,
          nox_mgs: isNaN(point.after_cat_nox_mf) ? 'No data' : point.after_cat_nox_mf, // NOx [mg/s]
          nox_gs: isNaN(point.after_cat_nox_mf_g) ? 'No data' : point.after_cat_nox_mf_g, // NOx [g/s]
          nox_cumulative_mgkm: isNaN(point.nox_cumulative_average) ? 'No data' : point.nox_cumulative_average, // NOx cumulative average [mg/km]
          nox_over_co2: isNaN(point.nox_over_co2_g_per_kg) ? 'No data' : point.nox_over_co2_g_per_kg, // NOx/CO2 [g/kg]
          nox_per_kwh: isNaN(point.nox_per_kwh) ? 'No data' : point.nox_per_kwh,
          pm10_mgkm: isNaN(point.pm_estimate_method1_perkm) ? 'No data' : point.pm_estimate_method1_perkm,
          pm10_mgkwh: isNaN(point.pm_estimate_method1_perkwh) ? 'No data' : point.pm_estimate_method1_perkwh,
          velocity_kmh: isNaN(point.velocity) ? 'No data' : point.velocity
        }

        if (this.showMap) {
          // Generate chartMapMapping
          this.chartMapMapping[index] = index

          const lat = point.gpslat
          const lon = point.gpslon

          // Check for lat/Lon validity
          if (!lat || !lon) continue

          // Check if a nextpoint exists
          const nextPoint = datapoints[index + 2]
          if (!nextPoint) continue

          // Check for outliers
          const coordinate = new google.maps.LatLng(lat, lon)
          const nextCoordinate = new google.maps.LatLng(
            datapoints[index + 2].gpslat,
            datapoints[index + 2].gpslon
          )
          const distanceInMeters = google.maps.geometry.spherical.computeDistanceBetween(
            coordinate,
            nextCoordinate
          )
          const velocityInMetersPerSecond = point.velocity / 3.6
          const deviationAccepted =
            (velocityInMetersPerSecond * this.mapMultiplier > distanceInMeters / 2 &&
             velocityInMetersPerSecond * this.mapMultiplier < distanceInMeters * 2) ||
            distanceInMeters < 25

          if (!deviationAccepted) continue

          // Correct starting point for GPS jumps
          let startCoordinate = coordinate
          if (
            lastAcceptedCoordinate !== null &&
            lastAcceptedCoordinate.lat() !== coordinate.lat() &&
            lastAcceptedCoordinate.lng() !== coordinate.lng()
          ) {
            startCoordinate = lastAcceptedCoordinate
          }
          lastAcceptedCoordinate = nextCoordinate

          // Add coordinates to polypoint
          polyPoint.coordinate = startCoordinate
          polyPoint.nextCoordinate = nextCoordinate

          // Extend bounds with coordinate
          bounds.extend(coordinate)
          pathCoordinates.push(coordinate)
        }

        this.polyPoints.push(Object.freeze(polyPoint))

        // Push data to Chart datasets
        for (const emissionKey of this.emissionOptions) {
          const index = this.emissionOptions.map(x => x.value).indexOf(emissionKey.value) - 1 // -1 because route takes up index 0
          if (emissionKey.value !== 'route') {
            this.chartDataArchive.datasets[index].data.push(polyPoint[emissionKey.value])
          }
        }
      }

      // Generate empty labels for ChartJS
      this.chartDataArchive.labels = [...Array(this.polyPoints.length)].map((x,i) => '')

      // Render that chart!
      this.chartData = this.chartDataArchive
      // Disable some most lines
      for (const emission of this.getOptions(this.vehicleType)) {
        if (emission.value === 'route') continue
        if (['co2_gs', 'nox_mgs', 'velocity_kmh'].indexOf(emission.value) === -1) {
          this.toggleLine(emission)
        }
      }

      // Do not draw a path if there is no path to draw
      if (!pathCoordinates) return
      const path = new google.maps.Polyline(
        Object.freeze(Object.assign({}, this.polyOptions, { path: pathCoordinates }))
      )

      this.currentPath.push(path)
      this.routePath = path

      const map = this.map
      path.setMap(map)
      map.fitBounds(bounds)

      // Setup infoWindow at start
      const infoWindow = new google.maps.InfoWindow()
      infoWindow.setPosition(pathCoordinates[0])
      infoWindow.setContent('START')
      infoWindow.open(this.map)

      if (this.zoom) map.setZoom(parseFloat(this.zoom))
      this.mapLoading = false

      // Add a listener on zoom and center
      // Check the visibility of each polygon and adjust the chart
      // Because of bound/zoom setting this redrawChart is triggered by default
      map.addListener('zoom_changed', () => {
        this.redrawChart(map)
      })
      map.addListener('center_changed', () => {
        this.redrawChart(map)
      })
    },
    redrawChart(map) {
      if (this.selected === 'route') return
      this.chartMapMapping = {}
      const visibleIndices = []
      const newChartDatasets = []
      // Generate a mapping of visible indices for the current path
      let counter = 0
      for (const [index, poly] of this.currentPath.entries()) {
        // Check if polylines are within bounds of the map
        const start = poly.getPath().getArray()[0]
        const end = poly.getPath().getArray()[1]
        if (map.getBounds().contains(start) && poly.getPath().getArray()[1]) {
          visibleIndices.push(index)
          this.chartMapMapping[counter] = index
          counter++
        }
      }
      // Determine new labels
      const newLabels = [...Array(visibleIndices.length)].map((x,i) => '')
      // Generate new dataset
      for (const dataArchive of this.chartDataArchive.datasets) {
        const clonedData = dataArchive.data.slice()
        const newData = visibleIndices.map(x => clonedData[x])
        newChartDatasets.push({
          label: dataArchive.label.slice(),
          id: dataArchive.id.slice(), fill: false, yAxisID: dataArchive.yAxisID.slice(), borderColor: dataArchive.borderColor.slice(),
          data: newData
        })
      }
      this.chartData = {
        labels: newLabels,
        datasets: newChartDatasets.filter(x => this.currentEmissions[x.id])
      }
      this.chartKey += 1 // Force rerender chart
      this.checkYAxes()
    },
    redrawMap(emission) {
      // Clear current path
      const map = this.map
      const currentPath = this.currentPath
      this.clickedPolyIndices = []
      for (const poly of currentPath) {
        google.maps.event.trigger(poly, 'mouseout', {})
        google.maps.event.clearListeners(poly, 'mouseover')
        google.maps.event.clearListeners(poly, 'mouseout')
        poly.setMap(null)
      }
      // Destroy to free up memory
      delete this.currentPath
      this.currentPath = []
      // Draw new path
      if (emission === 'route') {
        this.routePath.setMap(map)
        this.currentPath.push(this.routePath)
      } else {
        for (const [index, polyPoint] of this.polyPoints.entries()) {
          // Draw polylines for each 2 coordinates
          const poly = new google.maps.Polyline(
            Object.assign({}, this.polyOptions, {
              path: [polyPoint.coordinate, polyPoint.nextCoordinate],
              strokeColor: this.getStrokeColor(emission, polyPoint[emission], this.vehicleType)
            })
          )
          poly.setMap(map)
          this.currentPath.push(poly)
          this.generateTooltip(poly, polyPoint[emission], index)
        }
        // Generate chart
        const emissionObject = this.getOptions(this.vehicleType).filter(x => x.value === emission)[0]
        const dataArchive = this.chartDataArchive.datasets.filter(x => x.label === emissionObject.label)[0].data
        const clonedData = dataArchive.slice()
        for (const toggledEmission of Object.keys(this.currentEmissions)) {
          this.currentEmissions[toggledEmission] = toggledEmission === emissionObject.value
        }
        this.toggleButtonsKey = this.toggleButtonsKey += 1
        this.chartData = {
          labels: this.chartDataArchive.labels,
          datasets: [{
            label: emissionObject.label,
            backgroundColor: emissionObject.color,
            data: clonedData
          }]
        }
      }
      this.mapLoading = false

    },
    getEmissionTypes(vehicleType = 'LD') {
      const legend = Legend.data().legends.filter(legend => legend.type === vehicleType)[0]
      return legend.attributes
    },
    getStrokeColor(emission, value, vehicleType = 'LD') {
      const legend = Legend.data().legends.filter(legend => legend.type === vehicleType)[0]
      const rank = this.rank(legend[emission].steps, value)
      return legend[emission].colors[rank]
    },
    // Returns the rank of the given value in a sorted numeric array
    rank(array, n) {
      if (n < 0 || isNaN(n)) return 0
      for (const [index, step] of array.entries()) {
        if (n < step) return index - 1
      }
      return array.length - 1
    },
    generateTooltip(poly, content, index) {
      const that = this
      // Get poly half-way point
      const halfWay = google.maps.geometry.spherical.interpolate(
        poly.getPath().getArray()[0],
        poly.getPath().getArray()[1],
        0.5
      )
      // Setup infoWindow
      const infoWindow = new google.maps.InfoWindow()
      google.maps.event.addListener(poly, 'mouseover', event => {

        infoWindow.setPosition(halfWay)
        infoWindow.setContent(typeof content === 'number' ? content.toFixed(2) : content)
        infoWindow.open(this.map)
        // Style the infoWindow
        const innerWindow = document.querySelector('.gm-style-iw')
        if (innerWindow.nextElementSibling) {
          innerWindow.parentNode.removeChild(innerWindow.nextElementSibling)
        }
        innerWindow.style.setProperty('width', 'auto', 'important')
        innerWindow.style.setProperty('right', innerWindow.style.left, 'important')
        innerWindow.style.setProperty('text-align', 'center', 'important')
        // Trigger mouseover on graph
        const myChart = Chart.instances[0] || Chart.instances[Object.keys(Chart.instances)[0]]
        this.mapTriggeredHover = true

        // Open up a tooltip
        const tooltips = Object.keys(this.chartMapMapping).filter(x => this.chartMapMapping[x] === index)
        if (tooltips.length) this.openToolTip(myChart, parseInt(tooltips[0]))
      })
      // Close infoWindow on mouseout
      google.maps.event.addListener(poly, 'mouseout', () => {
        if (that.clickedPolyIndices.indexOf(index) === -1) {
          infoWindow.close()
          // Trigger mouseout on graph
          const myChart = Chart.instances[0] || Chart.instances[Object.keys(Chart.instances)[0]]
          this.mapTriggeredHover = true
          this.closeToolTip(myChart, this.chartMapMapping[index])
        }
      })
      google.maps.event.addListener(poly, 'click', () => {
        that.clickedPolyIndices.push(index)
        // Trigger mouseover on graph
        const myChart = Chart.instances[0] || Chart.instances[Object.keys(Chart.instances)[0]]
        this.mapTriggeredHover = true
        this.openToolTip(myChart, this.chartMapMapping[index])
      })
    },
    openToolTip(myChart, index) {
      let mouseMoveEvent, meta, point, rectangle, value

      meta = myChart.getDatasetMeta(0)
      rectangle = myChart.canvas.getBoundingClientRect()

      if (meta.data[index]) {
        point = meta.data[index].getCenterPoint()

        mouseMoveEvent = new MouseEvent('mousemove', {
          clientX: rectangle.left + point.x,
          clientY: rectangle.top + point.y
        })
        myChart.canvas.dispatchEvent(mouseMoveEvent)
      }
    },
    closeToolTip(myChart) {
      const mouseOutEvent = new MouseEvent('mouseout')
      return myChart.canvas.dispatchEvent(mouseOutEvent)
    },
    uygetOptions(vehicleType) {
      const options = [
        {
          label: 'CO2 [g/s]',
          value: 'co2_gs',
          color: '#3d3d3d' // black
        },
        {
          label: 'CO2 cumulative average [g/km]',
          value: 'co2_cumulative_gkm',
          color: '#C0C0C0' // grey
        },
        {
          label: 'NH3 [PPM]',
          value: 'nh3_ppm',
          color: '#808080' // dark grey
        },
        {
          label: 'Velocity [km/h]',
          value: 'velocity_kmh',
          color: '#4f4ff2' // blue
        },
        {
          label: 'Exhaust temperature [°C]',
          value: 'egt_c',
          color: '#FF0000' // red
        },
        {
          label: 'Engine coolant temperature [°C]',
          value: 'ect_c',
          color: '#F0A804' // orange
        },
        {
          label: 'NOx [mg/s]',
          value: 'nox_mgs',
          color: '#F4CC2C' // yellow
        },
        {
          label: 'NOx cumulative average [mg/km]',
          value: 'nox_cumulative_mgkm',
          color: '#008000' // green
        }
      ]
      if (vehicleType === 'HD') {
        options.push(
          {
            label: 'NOx [g/kWh]',
            value: 'nox_per_kwh',
            color: '#e240b7' // pink
          },
          {
            label: 'NOx [g/s]',
            value: 'nox_gs',
            color: '#cd4cf4' // purple
          },
          {
            label: 'NOx/CO2 [g/kg]',
            value: 'nox_over_co2',
            color: '#efb321' // orange-yellow
          },
          {
            label: 'PM10 estimate [mg/kwh]',
            value: 'pm10_mgkwh',
            color: '#800080' // purple
          }
        )
      } else if (vehicleType === 'LD') {
        options.push(
          {
            label: 'PM10 estimate [mg/km]',
            value: 'pm10_mgkm',
            color: '#800080' // purple
          }
        )
      }
      // Sort labels alphabetically
      const sortedOptions = options.sort((x,y) => x.label < y.label ? -1 : x.label > y.label ? 1 : 0)
      // Prepend route
      sortedOptions.unshift({ label: 'Route', value: 'route' })
      return sortedOptions
    },
    toggleLine(emission) {
      // Alter value of toggled emission
      this.currentEmissions[emission.value] = !this.currentEmissions[emission.value]
      // Generate new chart
      let newChartDatasets = []
      for (const emissionKey of Object.keys(this.currentEmissions)) {
        if (this.currentEmissions[emissionKey]) {
          const dataArchive = this.chartDataArchive.datasets.filter(x => x.id === emissionKey)[0]
          newChartDatasets.push({
            label: dataArchive.label.slice(),
            id: dataArchive.id.slice(), fill: false, yAxisID: dataArchive.yAxisID.slice(), borderColor: dataArchive.borderColor.slice(),
            data: dataArchive.data.slice()
          })
        }
      }
      this.chartData = {
        labels: this.chartDataArchive.labels,
        datasets: newChartDatasets
      }
      this.checkYAxes()
      this.chartKey += 1 // Force rerender chart
    },
    /**
      * For now this functions displays a Y-axis if one emission is selected
      * and disables all Y-axes if multiple emissions are selected.
      */
    checkYAxes() {
      // Check number of enabled emissions
      const countEnabled = Object.values(this.currentEmissions).filter(x => !!x).length
      if (countEnabled === 1) {
        // Display a y-axis
        for (const yAxis of this.chartOptions.scales.yAxes) {
          yAxis.display = false
        }
        for (const key of Object.keys(this.currentEmissions)) {
          if (this.currentEmissions[key]) {
            const yAxis = this.chartOptions.scales.yAxes.filter(x => x.id === key)[0]
            yAxis.display = true
            const dummyAxis = this.chartOptions.scales.yAxes.filter(x => x.id === 'dummy')[0]
            dummyAxis.display = false
          }
        }
      }
      else {
        // In case of multiple selected emissions disable the y-axes
        for (const yAxis of this.chartOptions.scales.yAxes) {
          yAxis.display = false
        }
        const dummyAxis = this.chartOptions.scales.yAxes.filter(x => x.id === 'dummy')[0]
        dummyAxis.display = true
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/Map/script.js